import {useMemo} from 'react';
import {STALE_TIME} from '@/utils/reactQueryClient';
import {
  SocialMediaPost,
  SocialStreamTab,
  CampaignStatsMetadata,
} from '@/types/socialMediaPost';
import campaignSocialMediaPostsFetchers, {
  CampaignSocialMediaPostsResponse,
} from '@/api/fetchers/campaignSocialMediaPosts';
import useInfiniteDataQuery from '@/hooks/use-infinite-data-query';

export const QUERY_KEY = 'campaignSocialMediaPosts';

const DEFAULT_PAGE_SIZE = 25;

interface UseCampaignSocialMediaPostsQueryParams {
  campaignId: string;
  socialStream?: SocialStreamTab;
  sort?: string;
  q?: string;
  perPage?: number;
  enabled?: boolean;
  startDate?: string;
  endDate?: string;
}

interface UseCampaignSocialMediaPostsQueryResult {
  posts: SocialMediaPost[];
  campaignStats: CampaignStatsMetadata | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  refetch: () => void;
  error: Error | null;
  totalCount: number;
}

function useCampaignSocialMediaPostsQuery({
  campaignId,
  socialStream = 'feed',
  sort = '-posted_at',
  q = '',
  perPage = DEFAULT_PAGE_SIZE,
  enabled = true,
  startDate,
  endDate,
}: UseCampaignSocialMediaPostsQueryParams): UseCampaignSocialMediaPostsQueryResult {
  // When 'all' is selected, omit the social_stream param so the API returns all post types
  const apiSocialStream = socialStream === 'all' ? undefined : socialStream;

  const queryResult = useInfiniteDataQuery({
    queryKey: [
      QUERY_KEY,
      campaignId,
      socialStream,
      sort,
      q,
      perPage,
      startDate,
      endDate,
    ],
    queryFn: ({pageParam = 1}: {pageParam?: number}) =>
      campaignSocialMediaPostsFetchers.fetchPosts({
        campaignId,
        socialStream: apiSocialStream,
        sort,
        q,
        perPage,
        page: pageParam,
        startDate,
        endDate,
      }),
    getNextPageParam: (lastPage: CampaignSocialMediaPostsResponse) =>
      lastPage.pagination.nextPage ?? undefined,
    enabled: enabled && !!campaignId,
    refetchOnWindowFocus: false,
    staleTime: STALE_TIME,
  });

  // useInfiniteDataQuery flattens the response, so pages is directly accessible
  const {pages = []} = queryResult as {
    pages?: CampaignSocialMediaPostsResponse[];
  };

  // Flatten all pages of posts into a single array
  const posts = useMemo(() => {
    if (!pages.length) {
      return [];
    }
    return pages.flatMap((page) => page.posts);
  }, [pages]);

  // Get campaign stats from the first page (they should be the same across pages)
  const campaignStats = useMemo(() => {
    if (!pages[0]) {
      return undefined;
    }
    return pages[0].campaignStats;
  }, [pages]);

  // Get total count from the first page's pagination
  const totalCount = useMemo(() => {
    if (!pages[0]) {
      return 0;
    }
    return pages[0].pagination.totalEntries;
  }, [pages]);

  return {
    posts,
    campaignStats,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isFetchingNextPage: queryResult.isFetchingNextPage,
    hasNextPage: queryResult.hasNextPage ?? false,
    fetchNextPage: queryResult.fetchNextPage,
    refetch: queryResult.refetch,
    error: queryResult.error as Error | null,
    totalCount,
  };
}

export default useCampaignSocialMediaPostsQuery;
